import { expandPermissions } from 'dashboard/constants/permissions.js';

export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

export const getCurrentAccount = ({ accounts } = {}, accountId = null) => {
  return accounts.find(account => Number(account.id) === Number(accountId));
};

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return expandPermissions(currentAccount.permissions || []);
};

// Quem pode LER cada recurso que a interface busca sozinha, espelhando o
// `index?` da policy do backend (#1625). A interface consulta isto ANTES de
// despachar a busca: sem a permissao o servidor responde 403, e a busca so
// sujava o console e deixava a secao vazia sem explicar por que.
//
// Uma regra por recurso, e mora so aqui. Componente que precisa saber se pode
// buscar pergunta `canReadResource` em vez de montar a propria lista: copia
// espalhada e como `ConversationBox` e `PopOverSearch` chegaram a checar a
// mesma coisa de dois jeitos.
//
// `defaultAgent`: a policy aceita `default_agent?` (atendente SEM funcao
// personalizada). No front isso e o papel `agent` na lista de permissoes, que o
// servidor so manda para quem nao tem funcao personalizada
// (Enterprise::AccountUser#permissions troca por `custom_role`).
// `allOf`: grupos que a policy cobra JUNTOS; dentro do grupo, basta um.
// O administrador passa sempre, como no `has_permission?` da ApplicationPolicy.
export const RESOURCE_READ_RULES = {
  // TaskPolicy#index? (tambem `counts?`)
  tasks: { defaultAgent: true, allOf: [['task_view', 'task_manage']] },
  // LabelPolicy#index?
  labels: { defaultAgent: true, allOf: [['label_view', 'label_manage']] },
  // CustomAttributeDefinitionPolicy#index? -- SEM `default_agent?`: o
  // atendente comum nao tem `custom_attribute_view` no piso
  // (PermissionCatalog::DEFAULT_VIEW_PERMISSIONS) e toma 403 aqui.
  customAttributeDefinitions: {
    defaultAgent: false,
    allOf: [['custom_attribute_view', 'custom_attribute_manage']],
  },
  // AgentBotPolicy#index?
  agentBots: { defaultAgent: true, allOf: [['bot_view', 'bot_manage']] },
  // ContactPolicy#index? -- show?, companies? e contactable_inboxes? caem nele.
  contacts: { defaultAgent: true, allOf: [['contact_view', 'contact_manage']] },
  // ScheduledMessagePolicy#index? -- tres familias juntas. Hoje
  // `scheduled_message_view` implica as outras duas no catalogo, mas a regra
  // segue a policy e nao a implicacao, que pode mudar.
  scheduledMessages: {
    defaultAgent: true,
    allOf: [
      ['scheduled_message_view', 'scheduled_message_manage'],
      ['contact_view', 'contact_manage'],
      [
        'conversation_manage',
        'conversation_participating_manage',
        'conversation_unassigned_manage',
      ],
    ],
  },
  // InternalChat::ChannelPolicy#module_available? (via InternalChat::Access.permitted?) -- SEM
  // `default_agent?`: o atendente comum alcanca o chat porque `internal_chat_view` esta no piso
  // (PermissionCatalog::DEFAULT_VIEW_PERMISSIONS); a funcao personalizada, so se marcada.
  internalChat: { defaultAgent: false, allOf: [['internal_chat_view']] },
};

export const canReadResource = (resource, userPermissions = []) => {
  const rule = RESOURCE_READ_RULES[resource];
  if (!rule) {
    throw new Error(`Unknown resource for canReadResource: ${resource}`);
  }
  if (userPermissions.includes('administrator')) return true;
  if (rule.defaultAgent && userPermissions.includes('agent')) return true;
  return rule.allOf.every(group => hasPermissions(group, userPermissions));
};

// Atalho para quem tem o usuario e a conta na mao (store, composable). Usuario
// sem `accounts` (ainda nao carregado) le como "sem permissao".
export const userCanReadResource = (resource, user, accountId) =>
  canReadResource(
    resource,
    user?.accounts ? getUserPermissions(user, accountId) : []
  );

const isPermissionsPresentInRoute = route =>
  route.meta && route.meta.permissions;

export const buildPermissionsFromRouter = (routes = []) =>
  routes.reduce((acc, route) => {
    if (route.name) {
      if (!isPermissionsPresentInRoute(route)) {
        // eslint-disable-next-line
        console.error(route);
        throw new Error(
          "The route doesn't have the required permissions defined"
        );
      }
      acc[route.name] = route.meta.permissions;
    }

    if (route.children) {
      acc = {
        ...acc,
        ...buildPermissionsFromRouter(route.children),
      };
    }

    return acc;
  }, {});

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};
